import '../styles/lesson.css';
import { el, formatLap } from './format';

/**
 * The driving lesson on screen: the offer before a first race, the cards
 * between attempts at turn 1 (the controls, "brake now", why an attempt went
 * wrong, well done, "your go", paused, the end), the captions and the speed
 * bar while driving, and the "✕ leave lesson" chip that stays up the whole
 * time. DOM only: main.ts decides what shows when, and does what the buttons
 * ask.
 *
 * Everything sits in the upper middle of the screen and never over the touch
 * pedals or the steering corner (a player must be able to press BRAKE while
 * "brake now" is up). Every card has a way out.
 *
 * On a phone there's little room above the road, so the lesson says less
 * there: while driving, one small strip in the sky (a short word, a slim bar,
 * the two speeds), and "brake now" as a small red banner. The shorter words
 * are drawn for every device and shown only on phones (styles/lesson.css).
 */

export type Controls = 'keys' | 'touch' | 'pad';
export type FailReason = 'wide' | 'wall' | 'spin' | 'stopped';
/** Where the driver is in a braking zone: the same union as the coach's (src/game/lesson.ts). */
type Phase = 'approach' | 'brake' | 'keep' | 'release' | 'turn' | 'gas' | 'done';

/** The controls a card can point at. */
type Control = 'gas' | 'brake' | 'left' | 'right' | 'steer' | 'retry' | 'leave' | 'confirm';
/** A line of words with controls in it: ['hold ', { k: 'brake' }] reads "hold ▼ S" on a keyboard. */
type Part = string | { k: Control };
type CardKind = 'offer' | 'intro' | 'ready' | 'freeze' | 'fail' | 'pass' | 'end';

/** The cards that wait for an answer (Enter, Space or a controller's A); "brake now" waits for the brake instead. */
const WAITING: ReadonlySet<CardKind> = new Set<CardKind>(['offer', 'intro', 'ready', 'fail', 'pass', 'end']);
/** For a moment after a card appears, Enter/Space/A don't answer it: a key already on its way down (the brake) isn't a choice. */
const ARM_MS = 350;
/** How long the "turn 1 ✓" stamp stays up (less if the next corner's braking starts first). */
const STAMP_MS = 1600;
/** A "brake now" card this soon after the last one comes up without its entrance: a brake tapper sees one card, not a flicker. */
const FREEZE_AGAIN_MS = 1500;

/** Keyboard keys, drawn as keycaps. ◀ and ▶ carry the text selector, or iPhones draw them as emoji. */
const KEYCAPS: Record<Control, string[]> = {
  gas: ['▲', 'W'],
  brake: ['▼', 'S'],
  left: ['◀\uFE0E', 'A'],
  right: ['▶\uFE0E', 'D'],
  steer: ['◀\uFE0E', '▶\uFE0E', 'A', 'D'],
  retry: ['R'],
  leave: ['Esc'],
  confirm: ['Enter'],
};
/** A controller: the shape of each control, and its label. */
const PAD: Record<Control, [string, string]> = {
  gas: ['trigger', 'RT'],
  brake: ['trigger', 'LT'],
  left: ['stick left', ''],
  right: ['stick right', ''],
  steer: ['stick', ''],
  retry: ['face y', 'Y'],
  leave: ['face b', 'B'],
  confirm: ['face a', 'A'],
};
/** Phones: small copies of the controls on the glass. A tap needs no key. */
const TOUCH: Record<Control, [string, string] | null> = {
  gas: ['pedal gas', 'GAS'],
  brake: ['pedal brake', 'BRAKE'],
  left: ['rail left', ''],
  right: ['rail right', ''],
  steer: ['rail', ''],
  retry: ['round', '↺'],
  leave: ['round leave', ''],
  confirm: null,
};
/** The intro's row of controls, and what each does. */
const TILES: [Control, string, string][] = [
  // control, its word (keys and controller), its word on a phone
  ['gas', 'gas', 'go'],
  ['brake', 'brake', 'slow'],
  ['steer', 'steer', 'slide to steer'],
  ['retry', 'retry', 'retry'],
  ['leave', 'leave', 'leave'],
];

const FAIL_TITLE: Record<FailReason, string> = {
  wide: 'you ran wide',
  wall: 'into the wall',
  spin: 'you spun',
  stopped: 'you stopped',
};

/** Draws one control for these controls, into `parent`. Null when there's nothing to draw (a tap needs no key). */
function glyph(controls: Controls, c: Control, parent?: HTMLElement): HTMLElement | null {
  if (controls === 'keys') {
    const g = el('span', 'l-glyph', parent);
    for (const k of KEYCAPS[c]) el('kbd', 'l-cap', g, k);
    return g;
  }
  const [shape, label] = controls === 'pad' ? PAD[c] : (TOUCH[c] ?? ['', '']);
  if (!shape) return null;
  const g = el('span', 'l-glyph', parent);
  const e = el('span', controls === 'pad' ? `l-pad ${shape}` : `l-touch ${shape}`, g, label);
  if (shape.startsWith('rail')) el('i', '', e);
  if (shape.endsWith('leave')) el('span', 'l-x', e);
  return g;
}

/** Words with controls drawn in among them. */
function words(parent: HTMLElement, controls: Controls, parts: Part[]): HTMLElement {
  for (const p of parts) {
    if (typeof p === 'string') parent.append(p);
    else glyph(controls, p.k, parent);
  }
  return parent;
}

const K = (k: Control): Part => ({ k });

/** "camilo’s", or a friend's lap shared without a name. */
function whose(name: string): string {
  return name ? `${name}’s` : 'your friend’s';
}

/**
 * A button that works mid-race, like the HUD's chips: it fires on the lift of
 * a finger (iOS drops the click while a thumb is on the gas), and on click for
 * a mouse. It never keeps the focus, so a later Enter can't press it again.
 */
function tapButton(parent: HTMLElement, cls: string, fire: () => void): HTMLButtonElement {
  const b = el('button', `${cls} interactive`, parent);
  b.type = 'button';
  b.tabIndex = -1;
  let tappedAt = -1e9;
  b.addEventListener('mousedown', (e) => e.preventDefault());
  b.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    tappedAt = performance.now();
    fire();
  });
  // And no click after the tap: whatever comes next may be under the finger by then.
  b.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  b.onclick = () => {
    if (performance.now() - tappedAt > 700) fire();
  };
  return b;
}

export class LessonUi {
  readonly root: HTMLElement;
  /** The offer: take the lesson. */
  onTake: () => void = () => {};
  /** The offer: no thanks, race now. */
  onSkip: () => void = () => {};
  /** Leave the lesson (the chip, or a card's button). */
  onLeave: () => void = () => {};
  /** Go on from a card: the intro, a try that went wrong, a pass. */
  onContinue: () => void = () => {};
  onRaceForReal: () => void = () => {};
  onHowToPlay: () => void = () => {};
  onSkipDemo: () => void = () => {};

  private leaveChip: HTMLButtonElement;
  private leaveKey: HTMLElement;
  private demo: HTMLElement;
  private demoSkip: HTMLButtonElement;
  private stamp: HTMLElement;
  private stampTimer = 0;
  private cue: HTMLElement;
  private cueWord: HTMLElement;
  private cueSub: HTMLElement;
  private cueShort: HTMLElement;
  private cueKey = '';
  private bar: HTMLElement;
  private barSpeed: HTMLElement;
  private barFill: HTMLElement;
  private barZone: HTMLElement;
  private barTarget: HTMLElement;
  private barNums: HTMLElement;
  private barText = '';
  private card: HTMLElement;
  private kind: CardKind | null = null;
  private shownAt = 0;
  private controls: Controls = 'keys';
  /** The controls the chip's key and the demo's skip are drawn for. */
  private keysFor: Controls | null = null;
  /** When a "brake now" or "keep braking" card was last on screen. */
  private freezeAt = -1e9;
  /** The freeze card's "hold the brake" label, and whether it's asking for a harder squeeze. */
  private holdEl: HTMLElement | null = null;
  private holdFor: Controls = 'keys';
  private squeeze = false;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'lesson-layer', parent);

    // The way out, up top for the whole lesson.
    this.leaveChip = tapButton(this.root, 'lesson-leave', () => this.onLeave());
    this.leaveChip.hidden = true;
    el('span', 'l-x', this.leaveChip);
    el('span', 'word', this.leaveChip, 'leave lesson');
    this.leaveChip.setAttribute('aria-label', 'leave lesson');
    this.leaveKey = el('span', 'key', this.leaveChip);

    // While driving, one column in the upper middle: the demo's strip, the stamp, the caption, the speed bar.
    // On a phone, one strip in the sky: the stamp or the caption's short word, then the bar with its numbers.
    const stack = el('div', 'lesson-stack', this.root);
    this.demo = el('div', 'lesson-demo', stack);
    this.demo.hidden = true;
    const demoWords = el('div', 'words', this.demo);
    el('div', 'title', demoWords, 'the instructor drives turn 1');
    el('div', 'text', demoWords, 'half speed · watch where it brakes');
    // A phone's shorter line.
    const brief = el('div', 'brief', demoWords);
    el('b', '', brief, 'the instructor');
    el('i', '', brief, ' · ');
    el('span', '', brief, 'half speed');
    this.demoSkip = tapButton(this.demo, 'l-go small', () => {
      this.showDemo(false);
      this.onSkipDemo();
    });
    this.drawSkip();
    // The stamp takes the caption's place, out of the column's flow: the bar under it never moves.
    this.stamp = el('div', 'lesson-stamp', stack);
    this.stamp.hidden = true;
    this.cue = el('div', 'lesson-cue', stack);
    this.cue.hidden = true;
    this.cueWord = el('div', 'word', this.cue);
    this.cueSub = el('div', 'sub', this.cue);
    // A phone's one word, with no key under it: the pedal that pulses says which to press.
    this.cueShort = el('div', 'short', this.cue);
    this.bar = el('div', 'lesson-bar', stack);
    this.bar.hidden = true;
    this.barSpeed = el('div', 'speed', this.bar);
    const track = el('div', 'track', this.bar);
    // The green zone at the far end: anywhere from its start (the most that's still fine) to the post is right.
    this.barZone = el('div', 'zone', track);
    this.barFill = el('div', 'fill', track);
    this.barTarget = el('div', 'target', this.bar);
    // A phone's small numbers after the bar ("183 → 56"), instead of the speed and target tags.
    this.barNums = el('div', 'nums', this.bar);

    this.card = el('div', 'lesson-card', this.root);
    this.card.hidden = true;

    // A phone turned on its side: the card may need to fit a new room.
    window.addEventListener('resize', () => this.fit());
    // Enter or Space answers a waiting card. Esc is main's (leave or skip); driving keys pass through untouched.
    window.addEventListener('keydown', (e) => {
      if (e.repeat || !this.cardUp) return;
      if (e.code !== 'Enter' && e.code !== 'NumpadEnter' && e.code !== 'Space') return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      this.confirm();
    });
  }

  /** A card is waiting for an answer: the offer, the intro, a try that went wrong, a pass, the end. */
  get cardUp(): boolean {
    return this.kind !== null && WAITING.has(this.kind);
  }

  /**
   * What Enter, Space or a controller's A does on the card that's up: take the
   * lesson, go on, race for real. Nothing on "brake now" (the brake answers
   * that), and nothing in the first moment after a card appears.
   */
  confirm(): void {
    if (!this.cardUp || performance.now() - this.shownAt < ARM_MS) return;
    const kind = this.kind;
    this.hideCard();
    if (kind === 'offer') this.onTake();
    else if (kind === 'end') this.onRaceForReal();
    else this.onContinue();
  }

  /**
   * The lesson is on: the leave chip with its key, and the HUD's clutter out
   * of the way (body.lesson). Off clears everything that belongs to a lesson
   * in progress; the end card stays.
   */
  setActive(on: boolean, controls: Controls): void {
    document.body.classList.toggle('lesson', on);
    this.showChip(on);
    this.setControls(controls);
    if (on) return;
    this.setCue(null);
    this.setBar(null);
    this.showDemo(false);
    this.hideStamp();
    if (this.kind !== 'end') this.hideCard();
  }

  /**
   * The player picked up another device: the chip's key, the demo's skip and
   * the cards still to come (the pass, the end, "your go") name its controls.
   * Shows and hides nothing. Cheap to call every frame.
   */
  setControls(controls: Controls): void {
    if (controls === this.keysFor) return;
    this.controls = controls;
    this.keysFor = controls;
    this.leaveKey.replaceChildren();
    if (controls !== 'touch') glyph(controls, 'leave', this.leaveKey);
    this.leaveKey.hidden = controls === 'touch';
    this.drawSkip();
  }

  /**
   * Before a first race: the lesson, or straight to racing. The HUD's clutter
   * and the pause button step aside already (body.lesson): pause here would
   * skip the lesson for good. The chip only comes with the lesson itself.
   */
  showOffer(o: { controls: Controls; challenger: string | null }): void {
    const c = o.controls;
    document.body.classList.add('lesson');
    const { main, side } = this.open('offer');
    el('div', 'l-kicker', main, 'driving lesson · about a minute');
    el('div', 'l-title', main, 'first race?');
    el('div', 'l-text', main, 'how to brake for turn 1, where most new drivers crash');
    if (o.challenger !== null) el('div', 'l-text l-then', main, `then race ${whose(o.challenger)} lap`);
    const act = el('div', 'l-actions', side);
    this.button(act, 'l-go', 'take the lesson', c, 'confirm', () => this.onTake());
    this.button(act, 'l-second', 'skip · race now', c, 'leave', () => this.onSkip());
    const way: Part[] = ['a ✕ to leave stays at the top the whole lesson'];
    if (c !== 'touch') way.push(' · or ', K('leave'));
    words(el('div', 'l-small', side), c, way);
    this.settle();
  }

  /** The controls, and what's coming. Main goes on when the player presses the gas; the button does too. */
  showIntro(o: { controls: Controls; dir: 1 | -1 }): void {
    const c = o.controls;
    const { main, side } = this.open('intro');
    el('div', 'l-kicker', main, 'driving lesson · the controls');
    el('div', 'l-title', main, 'turn 1 comes fast');
    el('div', 'l-text', main, `it’s a ${o.dir === 1 ? 'left' : 'right'} · we’ll brake for it together`);
    const tiles = el('div', 'l-tiles', side);
    for (const [ctl, word, phoneWord] of TILES) {
      const tile = el('div', `l-tile ${ctl}`, tiles);
      glyph(c, ctl, tile);
      el('span', 'label', tile, c === 'touch' ? phoneWord : word);
    }
    const act = el('div', 'l-actions', side);
    this.button(act, 'l-go', 'let’s go', c, c === 'touch' ? null : 'gas', () => this.onContinue());
    this.leaveButton(act, c);
    if (c === 'touch') words(el('div', 'l-small', side), c, ['or press ', K('gas')]);
    this.settle();
  }

  /**
   * A moment to get ready before the car moves again. 'held': the tab was put
   * away, and everything waited. 'afterDemo': the instructor is done, and the
   * next try is the player's. The gas goes on (main watches for it), and so
   * do the button, Enter and A.
   */
  showReady(o: { controls: Controls; kind: 'held' | 'afterDemo' }): void {
    const c = o.controls;
    const held = o.kind === 'held';
    const { main, side } = this.open('ready');
    el('div', 'l-kicker', main, held ? 'driving lesson' : 'turn 1');
    el('div', 'l-title', main, held ? 'paused' : 'your go');
    el('div', 'l-text', main, held ? 'carry on when you’re ready' : 'same corner · brake where the red starts, hold it till the bar is green');
    const act = el('div', 'l-actions', side);
    this.button(act, 'l-go', held ? 'carry on ▸' : 'let’s go ▸', c, c === 'touch' ? null : 'gas', () => this.onContinue());
    this.leaveButton(act, c);
    if (c === 'touch') words(el('div', 'l-small', side), c, ['or press ', K('gas')]);
    this.settle();
  }

  /**
   * The game stops: brake now, or keep braking. Pressing the brake goes on
   * (main watches for it). The speed to get down to is turn 1's own when
   * `final`, or a slower bend's on the way in ("slow to 184 first").
   */
  showFreeze(o: { why: 'brake' | 'keep'; controls: Controls; speedKmh: number; targetKmh: number; final: boolean }): void {
    const c = o.controls;
    // Tapping the brake on and off brings card after card: after the first, they just appear.
    const again = performance.now() - this.freezeAt < FREEZE_AGAIN_MS;
    const { main, side } = this.open('freeze');
    this.card.classList.toggle('again', again);
    this.freezeAt = performance.now();
    el('div', 'l-title', main, o.why === 'brake' ? 'brake now' : 'keep braking');
    this.holdEl = el('div', 'l-hold', main);
    this.holdFor = c;
    this.squeeze = false;
    this.drawHold();
    // A phone's banner says the numbers in one line instead of the two below: "183 → 56 km/h".
    const nums = el('div', 'l-nums', main);
    el('b', '', nums, String(Math.round(o.speedKmh)));
    el('i', '', nums, '→');
    el('b', '', nums, String(Math.round(o.targetKmh)));
    el('small', '', nums, o.final ? 'km/h' : 'km/h first');
    if (o.why === 'keep') el('div', 'l-text', side, 'you let go too soon · still too fast');
    this.facts(side, [
      [o.why === 'keep' ? 'still at' : 'you’re at', o.speedKmh, ''],
      o.final ? ['turn 1 takes up to', o.targetKmh, ''] : ['slow to', o.targetKmh, '', 'first'],
    ]);
    // On a phone's banner, just "✕ leave": the chip up top says the rest.
    const leave = this.leaveButton(side, c, 'l-link');
    leave.querySelector('.w')?.replaceChildren('leave', el('span', 'more', undefined, ' the lesson'));
    this.settle();
  }

  /**
   * On a freeze: the brake is pressed, but not far enough to go on (a
   * controller's trigger, lightly). The label asks for a harder squeeze, and
   * pulses. Cheap to call every frame.
   */
  setSqueeze(on: boolean): void {
    if (this.kind !== 'freeze' || !this.holdEl || on === this.squeeze) return;
    this.squeeze = on;
    this.drawHold();
  }

  /**
   * A try at turn 1 went wrong: what happened, kindly, with numbers, and what
   * to do next time. `early`: before the red, so it wasn't the braking.
   * `tooFast`: faster than a beginner takes that spot, so it was. `assisted`:
   * the brake was done for them, so the steering's the thing.
   */
  showFail(o: {
    why: FailReason;
    controls: Controls;
    arrivedKmh: number | null;
    targetKmh: number;
    braked: boolean;
    tries: number;
    next: 'retry' | 'demo';
    early: boolean;
    speedKmh: number;
    tooFast: boolean;
    assisted: boolean;
  }): void {
    const c = o.controls;
    const { main, side } = this.open('fail');
    el('div', 'l-kicker', main, `turn 1 · try ${o.tries}`);
    el('div', 'l-title', main, FAIL_TITLE[o.why]);
    // The speed at the corner, or where it went wrong on the way in; none from before the red, or for a stop (it'd be 0).
    const at: [string, number] | null =
      o.arrivedKmh !== null ? ['you arrived at', o.arrivedKmh] : o.early || o.why === 'stopped' ? null : ['you were at', o.speedKmh];
    if (at) {
      this.facts(main, [
        [at[0], at[1], o.tooFast ? 'over' : ''],
        ['this corner takes', o.targetKmh, ''],
      ]);
    }
    words(el('div', 'l-text', side), c, failAdvice(o));
    // Here the lesson puts the car back; in a race, the way out is up to the driver: how, once it's come up.
    const rescue = rescueLine(o.why, c);
    if (rescue) words(el('div', 'l-text l-rescue', side), c, rescue);
    if (o.next === 'demo') el('div', 'l-text l-then', side, 'the instructor shows you, at half speed · then it’s your go');
    const act = el('div', 'l-actions', side);
    this.button(act, 'l-go', o.next === 'demo' ? 'watch the instructor ▸' : 'try again ▸', c, 'confirm', () => this.onContinue());
    this.leaveButton(act, c);
    this.settle();
  }

  /**
   * Through turn 1. 'own': a card, next comes a try on your own. 'mastered':
   * a stamp for a moment, and the lap goes on.
   */
  showPass(o: { next: 'own' | 'mastered'; tries: number; assisted: boolean }): void {
    if (o.next === 'mastered') {
      this.hideStamp();
      this.stamp.replaceChildren();
      el('div', 'big', this.stamp, 'turn 1 ✓');
      const how = o.assisted ? 'made it' : o.tries <= 1 ? 'first try' : `mastered in ${o.tries} tries`;
      el('div', 'small', this.stamp, `${how} · now finish the lap`);
      this.stamp.hidden = false;
      this.stampTimer = window.setTimeout(() => this.hideStamp(), STAMP_MS);
      return;
    }
    const c = this.controls;
    const { main, side } = this.open('pass');
    el('div', 'l-kicker good', main, `turn 1 ✓ · try ${o.tries}`);
    el('div', 'l-title', main, 'that’s the way');
    el('div', 'l-text', main, 'next, one on your own · no “brake now” stop this time, just a shout of “brake!” · let go too soon and we’ll still stop you');
    const act = el('div', 'l-actions', side);
    this.button(act, 'l-go', 'on my own ▸', c, 'confirm', () => this.onContinue());
    this.leaveButton(act, c);
    this.settle();
  }

  /**
   * The instructor is driving: a strip that says so, and a way to skip it,
   * with its key (retry's: R, or a controller's Y). The chip still leaves.
   */
  showDemo(on: boolean, controls?: Controls): void {
    if (controls) this.setControls(controls);
    this.demo.hidden = !on;
  }

  /** The caption while driving, or null. Cheap to call every frame: it redraws only when it changes. */
  setCue(o: { phase: Phase; flash: boolean; dir: 1 | -1; controls: Controls; demo: boolean } | null): void {
    const w = o ? cueWords(o) : null;
    if (!o || !w) {
      this.cue.hidden = true;
      this.cueKey = '';
      return;
    }
    // The next corner's braking has started: the stamp makes way.
    if (w.tone === 'red') this.hideStamp();
    const key = `${o.phase}|${o.flash}|${o.dir}|${o.controls}|${o.demo}`;
    this.cue.hidden = false;
    if (key === this.cueKey) return;
    this.cueKey = key;
    this.cue.className = `lesson-cue ${w.tone}${o.flash ? ' flash' : ''}`;
    this.cueWord.textContent = w.word;
    this.cueSub.replaceChildren();
    words(this.cueSub, o.controls, w.sub);
    this.cueSub.hidden = w.sub.length === 0;
    this.cueShort.textContent = w.short;
  }

  /**
   * The speed bar: the speed now, the speeds that are right at the far end
   * (the corner's, when `final`; a bend's on the way in, before that): from
   * the beginner speed up to `topKmh`, the green zone, which starts `zone` of
   * the way along. `fill`: how much of the slowing to the beginner speed is
   * done (0 to 1). Green with a tick once in the zone.
   */
  setBar(o: { speedKmh: number; targetKmh: number; topKmh: number; fill: number; zone: number; ok: boolean; final: boolean } | null): void {
    if (!o) {
      this.bar.hidden = true;
      return;
    }
    this.bar.hidden = false;
    const speed = Math.max(0, Math.round(o.speedKmh));
    const target = Math.max(0, Math.round(o.targetKmh));
    const top = Math.max(target, Math.round(o.topKmh));
    // "92–101": the speeds that are right (just the one when they round the same).
    const right = top > target ? `${target}–${top}` : String(target);
    const text = `${speed}|${right}|${o.ok}|${o.final}`;
    if (text !== this.barText) {
      this.barText = text;
      this.barSpeed.replaceChildren();
      el('b', '', this.barSpeed, String(speed));
      el('i', '', this.barSpeed, o.ok ? '✓' : 'km/h');
      this.barTarget.replaceChildren();
      el('i', '', this.barTarget, o.final ? 'turn in at' : 'slow to');
      el('b', '', this.barTarget, right);
      this.barNums.replaceChildren();
      el('b', '', this.barNums, String(speed));
      el('i', '', this.barNums, '→');
      el('b', '', this.barNums, right);
      if (o.ok) el('em', '', this.barNums, '✓');
      this.bar.classList.toggle('ok', o.ok);
    }
    const zone = Math.max(0, Math.min(1, o.zone));
    this.barZone.hidden = zone >= 0.995;
    this.barZone.style.left = `${(zone * 100).toFixed(1)}%`;
    // Green: at least into the zone, further as the slowing goes on.
    const f = Math.max(0, Math.min(1, o.fill));
    this.barFill.style.transform = `scaleX(${(o.ok ? Math.max(f, zone) : f).toFixed(3)})`;
  }

  /** The lap's done and so is the lesson: how it went, the few rules, and the real race. */
  showEnd(o: { tries: number; lapMs: number | null; valid: boolean; challenger: string | null; assisted: boolean }): void {
    // The lesson's over: nothing left to leave.
    this.showChip(false);
    this.setCue(null);
    this.setBar(null);
    this.showDemo(false);
    this.hideStamp();
    const c = this.controls;
    const { main, side, foot } = this.open('end');
    // The sim has already started the next lap: its clock would say 0:00.0 over the practice lap's time.
    document.body.classList.add('lesson-end');
    el('div', 'l-kicker good', main, 'driving lesson ✓');
    el('div', 'l-title', main, 'lesson done');
    main.classList.add('has-facts');
    const facts = el('div', 'l-facts', main);
    const turn = el('div', 'l-fact good', facts);
    el('span', '', turn, 'turn 1');
    const done = el('b', '', turn, o.tries <= 1 ? 'first try' : `in ${o.tries} tries`);
    done.prepend(el('i', 'tick', undefined, '✓'));
    if (o.assisted) el('em', '', turn, 'with a hand on the brake');
    const lap = el('div', `l-fact${o.valid ? '' : ' invalid'}`, facts);
    el('span', '', lap, 'practice lap');
    el('b', '', lap, o.lapMs === null ? 'no time' : formatLap(o.lapMs));
    if (o.lapMs !== null && !o.valid) el('em', '', lap, 'off track · wouldn’t count');
    const rules = el('ol', 'l-rules', side);
    for (const r of ['one circuit and car a day, for everyone', 'your fastest clean lap counts', 'beat bronze → silver → gold, then challenge a friend']) {
      el('li', '', rules, r);
    }
    const notes = el('div', 'l-notes', side);
    el('p', '', notes, 'real laps start rolling at speed: brake early for turn 1');
    el('p', '', notes, 'the line and braking zones switch off now · turn them back on from the poster (line: on)');
    const act = el('div', 'l-actions', foot);
    this.button(act, 'l-go', o.challenger !== null ? `now race ${whose(o.challenger)} lap ▸` : 'race for real ▸', c, 'confirm', () => this.onRaceForReal());
    // Retry's key (R, Y) has nothing left to retry here: main makes it how to play.
    const how = tapButton(act, 'l-link', () => {
      this.hideCard();
      this.onHowToPlay();
    });
    how.textContent = 'how to play';
    if (c !== 'touch') glyph(c, 'retry', how);
    this.settle();
  }

  hideCard(): void {
    if (this.kind === 'freeze') this.freezeAt = performance.now();
    this.card.hidden = true;
    this.card.replaceChildren();
    this.kind = null;
    this.holdEl = null;
    this.root.classList.remove('card-up');
    document.body.classList.remove('lesson-card-up', 'lesson-end');
  }

  // ------------------------------------------------------------------ helpers

  /** Starts a card: three boxes to fill. Side by side on short, wide screens; one under the other elsewhere. */
  private open(kind: CardKind): { main: HTMLElement; side: HTMLElement; foot: HTMLElement } {
    if (this.kind === 'freeze') this.freezeAt = performance.now();
    document.body.classList.remove('lesson-end');
    this.holdEl = null;
    this.card.replaceChildren();
    this.card.className = `lesson-card ${kind}`;
    this.kind = kind;
    this.shownAt = performance.now();
    // Replay the entrance for a new card.
    this.card.hidden = true;
    void this.card.offsetWidth;
    this.card.hidden = false;
    this.root.classList.add('card-up');
    document.body.classList.add('lesson-card-up');
    return { main: el('div', 'l-main', this.card), side: el('div', 'l-side', this.card), foot: el('div', 'l-foot', this.card) };
  }

  /** After filling a card: empty boxes go, a card with a side column says so, and it's made to fit. */
  private settle(): void {
    for (const box of Array.from(this.card.children) as HTMLElement[]) box.hidden = box.childElementCount === 0;
    const side = this.card.querySelector<HTMLElement>('.l-side');
    this.card.classList.toggle('two', !!side && !side.hidden);
    this.fit();
  }

  /**
   * A card taller than the room between the chip and the touch controls (a
   * small phone on its side) shrinks until it fits, rather than run over the
   * pedals or lose its buttons. A shrunk card may also be laid out wider (it
   * ends up no wider on screen), so its lines wrap less and it needs less
   * shrinking.
   */
  private fit(): void {
    const c = this.card;
    c.style.scale = '';
    c.style.maxHeight = '';
    c.style.maxWidth = '';
    if (c.hidden) return;
    // While the content overflows, the card is as tall as its max-height: the room it has.
    const room = c.clientHeight;
    if (c.scrollHeight <= room + 1) return;
    // The widest it may be on screen: its max-width.
    c.style.width = '100000px';
    const wide = c.offsetWidth;
    c.style.width = '';
    c.style.maxHeight = 'none';
    for (let s = 1; s > 0.6; s -= 0.04) {
      c.style.maxWidth = `${(wide / s).toFixed(1)}px`;
      const h = c.offsetHeight;
      if (h * s <= room) {
        c.style.scale = Math.min(1, room / h, wide / c.offsetWidth).toFixed(3);
        return;
      }
    }
    c.style.maxWidth = `${(wide / 0.6).toFixed(1)}px`;
    c.style.scale = '0.6';
  }

  /** A card button: its words, then (keyboard, controller) the key that does the same. */
  private button(parent: HTMLElement, cls: string, text: string, controls: Controls, key: Control | null, fire: () => void): HTMLButtonElement {
    const b = tapButton(parent, cls, () => {
      this.hideCard();
      fire();
    });
    el('span', 'w', b, text);
    if (key && controls !== 'touch') glyph(controls, key, b);
    return b;
  }

  /** Every card's way out. */
  private leaveButton(parent: HTMLElement, controls: Controls, cls = 'l-second'): HTMLButtonElement {
    const b = this.button(parent, `${cls} l-leave`, 'leave the lesson', controls, 'leave', () => this.onLeave());
    b.prepend(el('span', 'l-x'));
    return b;
  }

  /**
   * A row of numbers, like the poster's medal times: label, then km/h, then a
   * note if there is one. The box holding them says so (a phone sets them
   * beside the title).
   */
  private facts(parent: HTMLElement, rows: [label: string, kmh: number, cls: string, note?: string][]): void {
    parent.classList.add('has-facts');
    const facts = el('div', 'l-facts', parent);
    for (const [label, kmh, cls, note] of rows) {
      const f = el('div', `l-fact${cls ? ' ' + cls : ''}`, facts);
      el('span', '', f, label);
      const b = el('b', '', f, String(Math.round(kmh)));
      el('small', '', b, 'km/h');
      if (note) el('em', '', f, note);
    }
  }

  /** The freeze card's one thing to do: hold the brake, or squeeze it harder. */
  private drawHold(): void {
    const h = this.holdEl;
    if (!h) return;
    const c = this.holdFor;
    h.replaceChildren();
    h.classList.toggle('squeeze', this.squeeze);
    words(h, c, [this.squeeze ? 'squeeze harder ' : c === 'pad' ? 'squeeze ' : 'hold ', K('brake')]);
  }

  /** The demo's skip, with the key that does the same (none on a phone: the button's a tap away). */
  private drawSkip(): void {
    const b = this.demoSkip;
    b.replaceChildren();
    el('span', 'w', b, 'skip ▸');
    if (this.controls !== 'touch') glyph(this.controls, 'retry', b);
  }

  /** The chip up top; cards start under it while it's there. */
  private showChip(on: boolean): void {
    this.leaveChip.hidden = !on;
    this.root.classList.toggle('chip', on);
  }

  private hideStamp(): void {
    window.clearTimeout(this.stampTimer);
    this.stamp.hidden = true;
  }
}

/** What to try next time, most useful first. */
/** After a wall or a spin: what to do about it in a race (in the lesson, the car is put back for you). */
function rescueLine(why: FailReason, c: Controls): Part[] | null {
  const back: Part[] = c === 'pad' ? ['squeeze ', K('brake')] : ['hold ', K('brake')];
  if (why === 'wall') return ['in a race, stuck? ', ...back, ' to back out · or ', K('retry'), ' for a fresh lap'];
  if (why === 'spin') return ['in a race, after a spin: ', K('retry'), ' for a fresh lap'];
  return null;
}

function failAdvice(o: { why: FailReason; braked: boolean; early: boolean; tooFast: boolean; assisted: boolean }): Part[] {
  const why = o.why;
  if (why === 'stopped') return ['let go of the brake once the bar is green'];
  // Off the road before the red: the steering, not the braking.
  if (o.early) return ['stay on the road until the red · small steering moves'];
  if (o.assisted) return ['the brake was done for you · follow the line and turn in where it bends'];
  if (!o.braked) return ['no brake this time · hold ', K('brake'), ' as soon as the red starts, and keep holding'];
  if (o.tooFast) {
    if (why === 'wide') return ['brake a bit earlier and hold it'];
    if (why === 'wall') return ['brake earlier, before the red ends'];
  }
  if (why === 'spin') return ['brake in a straight line first, then turn'];
  if (why === 'wall') return ['follow the line round · small steering moves'];
  return ['turn in when the line bends and follow it round'];
}

/**
 * The caption for a moment in the braking zone: its words, its colour, the
 * controls it points at, and a phone's short word for the same (no key there).
 */
function cueWords(o: { phase: Phase; flash: boolean; dir: 1 | -1; controls: Controls; demo: boolean }): { word: string; tone: 'plain' | 'red' | 'green'; sub: Part[]; short: string } | null {
  const side: Control = o.dir === 1 ? 'left' : 'right';
  const way = o.dir === 1 ? 'left' : 'right';
  // An arrow for the way, with the text selector (iPhones draw a bare ◀ as an emoji).
  const arrow = o.dir === 1 ? '◀︎' : '▶︎';
  const hold: Part[] = [o.controls === 'pad' ? 'squeeze ' : 'hold ', K('brake')];
  if (o.demo) {
    // The instructor's drive, told as it happens, with the control it's using.
    switch (o.phase) {
      case 'approach':
        return { word: 'watch the red', tone: 'plain', sub: ['the brakes go on where it starts'], short: 'watch the red' };
      case 'brake':
      case 'keep':
        return { word: 'brakes hard, straight', tone: 'red', sub: ['holding ', K('brake')], short: 'brakes' };
      case 'release':
        return { word: 'lets go of the brake', tone: 'green', sub: ['rolls to the corner'], short: 'lets go' };
      case 'turn':
        return { word: `turns ${way}`, tone: 'plain', sub: ['steering ', K(side)], short: `turns ${arrow}` };
      case 'gas':
        return { word: 'back on the gas', tone: 'green', sub: ['pressing ', K('gas')], short: 'gas' };
      default:
        return null;
    }
  }
  if (o.flash) return { word: 'brake!', tone: 'red', sub: hold, short: 'brake!' };
  switch (o.phase) {
    case 'approach':
      return { word: 'brake at the red', tone: 'plain', sub: [`a ${way} turn ahead`], short: 'brake at the red' };
    case 'brake':
      return { word: 'hold the brake', tone: 'red', sub: [...hold, ' till the bar is green'], short: 'hold' };
    case 'keep':
      return { word: 'keep braking', tone: 'red', sub: hold, short: 'brake' };
    // Slow enough, with the corner still a way off: off the brake, no steering yet.
    case 'release':
      return { word: 'let go of the brake', tone: 'green', sub: ['roll to the corner'], short: 'let go' };
    case 'turn':
      return { word: `turn ${way}`, tone: 'plain', sub: ['steer ', K(side)], short: `turn ${arrow}` };
    case 'gas':
      return { word: 'back on the gas', tone: 'green', sub: [K('gas')], short: 'gas' };
    default:
      return null;
  }
}
